import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// newly added for Share as Link Dialog
import LinkIcon from '@mui/icons-material/Link';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogContentText,
  LinearProgress,
  Typography,
  createFilterOptions
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import copy from 'copy-to-clipboard';
import React from 'react';
import { useSelector } from 'react-redux';
import { getPoratlHost } from 'src/components/common/hooks/useUtil';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { AppState, store, useAppSelector } from 'src/store';
import { setSnackbarMessage } from 'src/store/snackbarMessage';
import DocManagementService from '../documentUpload/service/DocManagementService';
import './ModelViewer.scss';

const SendasLink = (props: any) => {
  const docService = new DocManagementService();
  const MAX_EMAIL_IDS = 1;
  const { openSendAsLinkDialog, setSendAsLinkDialog, documentDetails } = props;
  // for Send as Link Dialog
  const [openSendDialog, setOpenSendDialog] = React.useState(openSendAsLinkDialog);
  const [usersList, setUsersList] = React.useState([]);
  const [selectedUsersList, setSelectedUsersList] = React.useState<any>([]);
  const [isProcessing, setProcessing] = React.useState(false);
  const [isValidEmails, setValidEmails] = React.useState(true);
  const [message, setMessage] = React.useState('');
  const user: any = useAppSelector(state => state.user.user);
  const documentDetail: any = useSelector((state: AppState) => state.documentDetail);
  const isfLevel = documentDetail?.data[0]?.isf_doc_level;
  const selectedStudy = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);
  const entity = documentDetail?.data[0]?.isf_document_name;
  const [openConfirmExit, setOpenConfirmExit] = React.useState(false);
  const [anchorElCopied, setAnchorElCopied] = React.useState<HTMLButtonElement | null>(null);
  const openCopied = Boolean(anchorElCopied);
  const filter = createFilterOptions<any>();
  React.useEffect(() => {
    getAllUsers();
  }, []);

  const handleCloseSendDialog = async () => {
    if (selectedUsersList.length || message || anchorElCopied) {
      setOpenConfirmExit(true);
    } else {
      setProcessing(false);
      setOpenSendDialog(false);
      setSendAsLinkDialog(false);
    }
  };

  const handleSendEmailDialog = async () => {
    setProcessing(true);
    const resp: any = await docService.sendEmail(
      documentDetails?.hashed_uri_key,
      documentDetails?.isf_document_name,
      message,
      selectedUsersList,
      user,
      selectedStudy,
      selectedCountry,
      selectedSite,
      isfLevel,
      process.env.REACT_APP_NAME,
      'DocumentShare',
      entity
    );
    if (resp?.data) {
      store.dispatch(setSnackbarMessage('Page shared successfully', 'success'));
      setOpenSendDialog(false);
      setSendAsLinkDialog(false);
      setProcessing(false);
    } else {
      store.dispatch(setSnackbarMessage('Error in sharing page.', 'error'));
      setProcessing(false);
    }
  };

  const handleClickCopied = (event: React.MouseEvent<HTMLButtonElement>) => {
    const appName = 'ISF';
    copy(`${getPoratlHost()}?application=${appName}&key=${documentDetails?.hashed_uri_key}`);
    setAnchorElCopied(event.currentTarget);
    setTimeout(() => {
      handleCloseCopied();
    }, 2000);
  };

  const handleCloseCopied = () => {
    setAnchorElCopied(null);
  };

  const getAllUsers = async () => {
    setProcessing(true);
    const response: any = await docService.getAllUsers(
      selectedStudy,
      isfLevel,
      documentDetail.data[0].isf_artifact_name,
      selectedCountry,
      selectedSite
    );
    const filtered = response.data
      .filter((el: any) => {
        return el.firstName !== null;
      })
      .map((ele: any) => {
        return {
          id: ele.id,
          email: ele.email,
          firstName: ele.firstName,
          lastName: ele.lastName,
          user_name: `${ele.firstName} ${ele.lastName}`
        };
      });
    if (response?.data) {
      setUsersList(filtered);
    }
    setProcessing(false);
  };

  return (
    <div>
      <PopupComponent open={openSendDialog} maxWidth="sm">
        <DialogTitle>Share</DialogTitle>
        <DialogContent className="share-dialog">
          {isProcessing && (
            <Box sx={{ width: '100%' }} mb={2}>
              <LinearProgress />
            </Box>
          )}
          <Autocomplete
            disabled={isProcessing}
            multiple
            id="tags-standard"
            options={usersList}
            getOptionLabel={(option: any) => `${option?.firstName ?? ``} ${option?.lastName ?? ``} (${option?.email})`}
            clearOnBlur
            freeSolo
            value={selectedUsersList}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              const isExisting = options.some(option => inputValue === option?.email);
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  email: `Add ${inputValue}`
                });
              }
              return filtered;
            }}
            renderTags={(value: readonly any[], getTagProps) =>
              value.map((option: any, index: number) => (
                <div key={index}>
                  <Chip variant="outlined" label={option?.email || option} {...getTagProps({ index })} />
                </div>
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                label="Enter Name or Email"
                placeholder="Name/Email of person"
                disabled={isProcessing}
                required
              />
            )}
            onChange={(event, newValue) => {
              const regex = new RegExp('[a-z0-9].[a-z0-9]+@[a-z]+.[a-z]{2,3}');
              let isValidEmail = true;
              const userTemp: any = [];
              newValue?.forEach(value => {
                if (typeof value === 'string') {
                  if (regex.test(value)) {
                    userTemp.push({ email: value });
                  }
                } else if (value.inputValue) {
                  if (regex.test(value.inputValue)) {
                    userTemp.push({ email: value.inputValue });
                  }
                } else {
                  const email = value?.email || value;
                  userTemp.push({
                    email: value.email,
                    id: value.id,
                    firstName: value.firstName,
                    lastName: value.lastName,
                    user_name: `${value.firstName ?? ``} ${value.lastName ?? ``}`
                  });
                  if (!regex.test(email)) {
                    isValidEmail = false;
                    return;
                  }
                }
              });
              setValidEmails(isValidEmail);
              if (isValidEmail) {
                setSelectedUsersList(userTemp);
              }
            }}
          />
          {!isValidEmails && <Alert severity="error">Entered mail ID is not valid.</Alert>}

          <TextField
            id="standard-multiline-static"
            multiline
            margin="normal"
            minRows={2}
            maxRows={5}
            placeholder="Type your message here..."
            fullWidth
            variant="standard"
            sx={{ mt: 5 }}
            onChange={e => {
              e.target.value = e.target.value?.trimStart();
              setMessage(e.target.value);
            }}
            disabled={isProcessing}
            inputProps={{
              maxLength: 300
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', mt: 3, pl: 2, pr: 2 }}>
          <div>
            <Button onClick={handleClickCopied} startIcon={<LinkIcon />} sx={{ ml: 0.5 }} className="nouppercase-btn">
              Copy Shareable Link
            </Button>

            <Popover
              open={openCopied}
              anchorEl={anchorElCopied}
              onClose={handleCloseCopied}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              className="copiedPop"
            >
              <Typography sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon /> &nbsp; Link Copied to Clipboard
              </Typography>
            </Popover>
          </div>
          <div>
            <Button onClick={handleCloseSendDialog} className="grey-btn" disabled={isProcessing}>
              Close
            </Button>
            <Button
              onClick={handleSendEmailDialog}
              disabled={selectedUsersList?.length < MAX_EMAIL_IDS || isProcessing}
            >
              Share
            </Button>
          </div>
        </DialogActions>
      </PopupComponent>
      <PopupComponent
        open={openConfirmExit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="confirm-modal"
        child={true}
      >
        <DialogTitle id="alert-dialog-title">
          <b>Please Confirm</b>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All unsaved changes will be lost. Are you sure you want to exit before saving your changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirmExit(false);
            }}
            className="grey-btn"
          >
            CLOSE
          </Button>
          <Button
            onClick={() => {
              setOpenSendDialog(false);
              setSendAsLinkDialog(false);
              setOpenConfirmExit(false);
            }}
          >
            EXIT
          </Button>
        </DialogActions>
      </PopupComponent>
    </div>
  );
};

export default SendasLink;
